import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { ORDER_STATUSES, CATEGORIES } from '../../constants';
import { formatTimeAgo, formatPrice } from '../../utils/formatters';
import { useApi } from '../../hooks/useApi';

const STATUS_FLOW = ['confirmed', 'in_production', 'ready', 'shipped', 'delivered'];

export function OrdersScreen({ onNavigate }) {
  const { isAuthenticated, login } = useAuth();
  const api = useApi();
  const [role, setRole] = useState('buyer'); // 'buyer' | 'maker'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    async function fetchOrders() {
      setLoading(true);
      try {
        const res = await api.orders.list({ role });
        if (!cancelled) setOrders(res.data || []);
      } catch (err) {
        console.error('Orders fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchOrders();
    return () => { cancelled = true; };
  }, [isAuthenticated, role]);

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', marginTop: '40px' }}>
        <p style={{ fontSize: '40px', marginBottom: '16px' }}>🔒</p>
        <p style={{ color: theme.text, fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
          Skráðu þig inn til að sjá pantanir
        </p>
        <button
          onClick={() => login()}
          style={{
            marginTop: '12px',
            background: theme.accent,
            color: '#000',
            border: 'none',
            padding: '12px 24px',
            borderRadius: theme.radiusSm,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Innskrá
        </button>
      </div>
    );
  }

  const handleAdvanceStatus = async (order) => {
    const currentIdx = STATUS_FLOW.indexOf(order.status);
    if (currentIdx < 0 || currentIdx >= STATUS_FLOW.length - 1) return;
    const nextStatus = STATUS_FLOW[currentIdx + 1];

    setUpdatingId(order.id);
    try {
      await api.orders.updateStatus(order.id, { status: nextStatus });
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: nextStatus } : o
      ));
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const getNextStatusLabel = (status) => {
    const labels = {
      confirmed: 'Byrja framleiðslu',
      in_production: 'Merkja tilbúið',
      ready: 'Merkja sent',
      shipped: 'Merkja afhent',
    };
    return labels[status];
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '16px' }}>
        Pantanir
      </h2>

      {/* Role Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        background: theme.bgCard,
        borderRadius: theme.radius,
        padding: '4px',
      }}>
        {[
          { id: 'buyer', label: 'Kaupandi' },
          { id: 'maker', label: 'Smiður' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setRole(t.id)}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: theme.radiusSm,
              border: 'none',
              background: role === t.id ? theme.accent : 'transparent',
              color: role === t.id ? '#000' : theme.textSecondary,
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.textMuted }}>Hleð...</div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>📦</p>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>
            Engar pantanir enn
          </p>
          <p style={{ fontSize: '13px', color: theme.textSecondary, marginTop: '4px' }}>
            {role === 'buyer' ? 'Sendu beiðni og samþykktu tilboð' : 'Sendu tilboð í beiðnir'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {orders.map(order => {
            const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.confirmed;
            const cat = CATEGORIES.find(c => c.id === order.category);
            const nextLabel = role === 'maker' ? getNextStatusLabel(order.status) : null;
            const canAdvance = role === 'maker' && nextLabel;
            const canConfirmDelivery = role === 'buyer' && (order.status === 'shipped' || order.status === 'ready');

            return (
              <div key={order.id} style={{
                background: theme.bgCard,
                border: `1px solid ${theme.border}`,
                borderRadius: theme.radius,
                padding: '14px',
              }}>
                {/* Status + Time */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{
                    fontSize: '12px',
                    padding: '3px 8px',
                    borderRadius: '10px',
                    background: `${status.color}20`,
                    color: status.color,
                    fontWeight: 600,
                  }}>
                    {status.label}
                  </span>
                  <span style={{ fontSize: '11px', color: theme.textMuted }}>
                    {formatTimeAgo(order.created_at)}
                  </span>
                </div>

                {/* Title + Price */}
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: theme.text, marginBottom: '4px' }}>
                  {order.title || 'Pöntun'}
                </h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: theme.categories[order.category] || theme.textMuted }}>
                    {cat?.icon} {cat?.label}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: theme.accent }}>
                    {formatPrice(order.total_price)}
                  </span>
                </div>

                {/* Status Timeline */}
                <div style={{ display: 'flex', gap: '2px', marginTop: '12px' }}>
                  {STATUS_FLOW.map((s, i) => {
                    const currentIdx = STATUS_FLOW.indexOf(order.status);
                    return (
                      <div key={s} style={{
                        flex: 1,
                        height: '4px',
                        borderRadius: '2px',
                        background: i <= currentIdx ? status.color : theme.border,
                      }} />
                    );
                  })}
                </div>

                {/* Action Buttons */}
                {canAdvance && (
                  <button
                    onClick={() => handleAdvanceStatus(order)}
                    disabled={updatingId === order.id}
                    style={{
                      marginTop: '10px',
                      width: '100%',
                      padding: '10px',
                      background: theme.accent,
                      color: '#000',
                      border: 'none',
                      borderRadius: theme.radiusSm,
                      fontWeight: 600,
                      cursor: updatingId === order.id ? 'wait' : 'pointer',
                      opacity: updatingId === order.id ? 0.6 : 1,
                    }}
                  >
                    {updatingId === order.id ? 'Uppfæri...' : nextLabel}
                  </button>
                )}
                {canConfirmDelivery && (
                  <button
                    onClick={() => handleAdvanceStatus({ ...order, status: order.status })}
                    disabled={updatingId === order.id}
                    style={{
                      marginTop: '10px',
                      width: '100%',
                      padding: '10px',
                      background: theme.success,
                      color: '#fff',
                      border: 'none',
                      borderRadius: theme.radiusSm,
                      fontWeight: 600,
                      cursor: updatingId === order.id ? 'wait' : 'pointer',
                      opacity: updatingId === order.id ? 0.6 : 1,
                    }}
                  >
                    {updatingId === order.id ? 'Uppfæri...' : 'Staðfesta afhendingu'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
